import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdCheck } from 'react-icons/md';

const RatingLoader = ({ onComplete, isApiComplete = false }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    if (isComplete) return;

    const duration = 3000; // 3 seconds
    const interval = 50; // Update every 50ms for smooth animation
    const increment = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 99) {
          // Only complete if API call is also finished
          if (isApiComplete) {
            // Jump to 100% and complete
            setIsComplete(true);
            clearInterval(timer);
            // Call onComplete callback after 2 seconds
            setTimeout(() => {
              onComplete && onComplete();
            }, 2000);
            return 100;
          }
          // If API not complete, stay at 99% and don't show success yet
          return 99;
        }
        return Math.min(prev + increment, 99);
      });
    }, interval);

    return () => clearInterval(timer);
  }, [isComplete, isApiComplete, onComplete]);

  // Animate dots appearing one by one
  useEffect(() => {
    if (isComplete) return;

    const dotTimer = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4); // 0, 1, 2, 3, then back to 0
    }, 500); // Change every 500ms

    return () => clearInterval(dotTimer);
  }, [isComplete]);

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const renderDots = () => {
    const dots = [];
    for (let i = 0; i < 3; i++) {
      dots.push(
        <motion.span
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: i < dotCount ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className="inline-block"
        >
          .
        </motion.span>
      );
    }
    return dots;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center">
        <AnimatePresence mode="wait">
          {!isComplete ? (
            <motion.div
              key="progress"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="relative w-40 h-40 mb-8"
            >
              {/* Background circle */}
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 140 140">
                <circle
                  cx="70"
                  cy="70"
                  r={radius}
                  stroke="#E5E7EB"
                  strokeWidth="8"
                  fill="none"
                />
                {/* Progress circle */}
                <circle
                  cx="70"
                  cy="70"
                  r={radius}
                  stroke="#4F46E5"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-100 ease-out"
                />
              </svg>
              
              {/* Center content */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-2">
                  <div className="text-3xl font-bold text-gray-800 mb-2">
                    {Math.round(progress)}%
                  </div>
                  <div className="text-xs text-gray-500 font-medium leading-tight">
                    {progress >= 99 && !isApiComplete ? "Waiting..." : "Submitting review"}
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center mb-8"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 20,
                  delay: 0.2 
                }}
                className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <MdCheck className="w-10 h-10 text-white" />
              </motion.div>
              <div className="text-xl font-bold text-gray-800 mb-2">
                Review Submitted!
              </div>
              <div className="text-sm text-gray-600 font-medium">
                Thank you for your feedback
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Animated dots text */}
        {!isComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center px-4 mb-6"
          >
            <div className="text-gray-600">
              <div className="text-sm font-medium">
                Submitting Review
                <span className="ml-1">
                  {renderDots()}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Success confirmation text */}
        {isComplete && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center px-4"
          >
            <div className="text-green-600 font-semibold text-sm">
              Your review has been successfully submitted!
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RatingLoader;
